import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import djiBridge from '../utils/cloud-sample-DjiBridge';
import cloudModule, { EComponentName, EStatusValue } from '../utils/cloud-sample-cloudModule';
import { setAuth, clearAuth } from '../utils/auth';

// Define localStorage keys exactly as in pilot-index.vue
const ELocalStorageKey = {
  Token: 'x-auth-token',
  Username: 'username',
  UserId: 'user_id',
  WorkspaceId: 'workspace_id',
  WorkspaceName: 'workspace_name',
  WorkspaceDesc: 'workspace_desc',
  PlatformName: 'platform_name',
  GatewayOnline: 'gatewayOnline',
  Device: 'device',
  Flag: 'flag',
  WebsdkNgInstallId: 'websdk_ng_install_id'
};

// API endpoints using exact paths from manage.ts
const HTTP_PREFIX = '/manage/api/v1';
const LOGIN_URL = `${HTTP_PREFIX}/login`;
const USER_INFO_URL = `${HTTP_PREFIX}/users/current`;

// DJI Configuration
const DJI_CONFIG = {
  appId: "159162",
  appKey: "b60f8b74980f0ce70bb7bbf7745e0d2",
  appLicense: "uYfI2feXZsct/MCi4/78VoBXikrOV33CfVBrJHoGjNsz9FA06vSDsbCKo+4CibUQ2IMEmqlRL6Y0qcHazP/yoXrW7GzGE8AZD7/w3g7U0sjVg3ekz1wINZ5M/dN4bL48hbQH8GrSihUGD9xefCV4w1sXm3nGN4k1wUHQIonYCZw="
};

/**
 * Verify DJI license - exactly as in pilot-index.vue
 */
const verifyLicense = () => {
  const isVerified = djiBridge.platformVerifyLicense(
    DJI_CONFIG.appId, 
    DJI_CONFIG.appKey, 
    DJI_CONFIG.appLicense
  ) && djiBridge.isPlatformVerifySuccess();
  
  if (isVerified) {
    console.log('[CLOUD-SAMPLE] The license verification is successful.');
  } else {
    console.error('[CLOUD-SAMPLE] Filed to verify the license. Please check license whether the license is correct, or apply again.');
  }
  
  return isVerified;
};

/**
 * Cloud Sample PilotLogin Component
 * Provides user authentication and initializes the DJI JSBridge
 * Following exact sequence from pilot-index.vue
 */
const CloudSamplePilotLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('pilot');
  const [password, setPassword] = useState('pilot123');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);

  // Check for existing authentication and verify license on mount
  useEffect(() => {
    // First, initialize loading state
    setLoading(true);
    
    // STEP 1: Verify license
    console.log('[CLOUD-SAMPLE] Verifying DJI license');
    const verified = verifyLicense();
    setIsVerified(verified);
    
    if (!verified) {
      setError('Please verify the license firstly.');
      setLoading(false);
      return;
    }
    
    // STEP 2: Set platform information
    console.log('[CLOUD-SAMPLE] Setting platform information');
    djiBridge.platformSetInformation('Cloud Api Platform', '', '');
    
    // STEP 3: Check localStorage for existing token
    console.log('[CLOUD-SAMPLE] Checking localStorage for existing token');
    const storedToken = localStorage.getItem(ELocalStorageKey.Token);
    
    if (storedToken) {
      console.log('[CLOUD-SAMPLE] Found token in localStorage:', storedToken.substring(0, 10) + '...');
      // STEP 4: If token exists, validate/refresh it
      validateExistingToken(storedToken);
    } else {
      // If no token, just wait for user to log in
      console.log('[CLOUD-SAMPLE] No existing token, waiting for login');
      setLoading(false);
    }
  }, [navigate]);

  // Validate an existing token by checking current user
  const validateExistingToken = async (token) => {
    try {
      // Set auth token in header
      const headers = {};
      headers[ELocalStorageKey.Token] = token;
      
      console.log('[CLOUD-SAMPLE] Validating existing token with user info request');
      console.log('[CLOUD-SAMPLE] Request URL:', USER_INFO_URL);
      
      // Make request to get current user info
      const response = await axios.get(USER_INFO_URL, { headers });
      console.log('[CLOUD-SAMPLE] User info response:', response.data);
      
      // Check if response is successful with code 0
      if (response.data && response.data.code === 0) {
        console.log('[CLOUD-SAMPLE] Token is valid, user info retrieved successfully');
        
        // Store token in auth utility
        setAuth(token, response.data.data);
        
        // STEP 5: Set API component parameters
        console.log('[CLOUD-SAMPLE] Setting API component parameters with token');
        djiBridge.setComponentParam(EComponentName.Api, {
          host: window.location.origin,
          token: token
        });
        
        // STEP 6: Load API component
        console.log('[CLOUD-SAMPLE] Loading API component');
        const apiLoaded = djiBridge.loadComponent(
          EComponentName.Api, 
          djiBridge.getComponentParam(EComponentName.Api)
        );
        
        if (!apiLoaded) {
          console.error('[CLOUD-SAMPLE] Failed to load API component');
          setError('Failed to initialize API module');
          setLoading(false);
          return;
        }
        
        // Compare token and host values with what was sent
        const sentToken = token;
        const sentHost = window.location.origin;
        
        // Get current values from the bridge
        try {
          // Note: The JSBridge interface returns a JSON string that needs to be parsed
          const tokenResponse = djiBridge.getToken();
          console.log('[CLOUD-SAMPLE] Raw token response:', tokenResponse);
          const parsedTokenResponse = JSON.parse(tokenResponse);
          const currentToken = parsedTokenResponse.code === 0 ? parsedTokenResponse.data : null;
          
          const hostResponse = djiBridge.getHost();
          console.log('[CLOUD-SAMPLE] Raw host response:', hostResponse);
          const parsedHostResponse = JSON.parse(hostResponse);
          const currentHost = parsedHostResponse.code === 0 ? parsedHostResponse.data : null;
          
          // Log comparison results
          console.log('[CLOUD-SAMPLE] API Token Comparison:');
          console.log('[CLOUD-SAMPLE] Sent token:', sentToken ? `${sentToken.substring(0, 10)}...` : 'null');
          console.log('[CLOUD-SAMPLE] Current token from bridge:', currentToken ? `${currentToken.substring(0, 10)}...` : 'null');
          console.log('[CLOUD-SAMPLE] Token match:', sentToken === currentToken);
          
          console.log('[CLOUD-SAMPLE] API Host Comparison:');
          console.log('[CLOUD-SAMPLE] Sent host:', sentHost);
          console.log('[CLOUD-SAMPLE] Current host from bridge:', currentHost);
          console.log('[CLOUD-SAMPLE] Host match:', sentHost === currentHost);
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error comparing token and host values:', error);
        }
        
        // Navigate to dashboard since token is valid
        navigate('/cloud-sample-dashboard');
      } else {
        console.log('[CLOUD-SAMPLE] Token validation failed: Status OK but code not 0');
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem(ELocalStorageKey.Token);
        setLoading(false);
      }
    } catch (err) {
      console.error('[CLOUD-SAMPLE] Error validating token:', err);
      console.log('[CLOUD-SAMPLE] Error response:', err.response ? err.response.data : 'No response data');
      
      // If 401 unauthorized, token is expired
      if (err.response && err.response.status === 401) {
        console.log('[CLOUD-SAMPLE] Token expired (401), please log in');
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem(ELocalStorageKey.Token);
      } else {
        setError('Error validating your session. Please try again.');
      }
      
      setLoading(false);
    }
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!isVerified) {
      setError('Please verify the license firstly.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Make login request to /manage/api/v1/login (equivalent to Vue's login() call)
      const loginData = {
        username, 
        password,
        flag: 2 // EUserType.Pilot
      };
      
      console.log('[CLOUD-SAMPLE] Making login request with data:', { ...loginData, password: '****' });
      console.log('[CLOUD-SAMPLE] Login URL:', LOGIN_URL);
      
      const response = await axios.post(LOGIN_URL, loginData);
      console.log('[CLOUD-SAMPLE] Login response:', response.data);
      
      // Check if login was successful (code 0)
      if (response.data && response.data.code === 0) {
        // Extract all available data from the response
        const responseData = response.data.data;
        
        // Get the token from response
        const accessToken = responseData.access_token;
        console.log('[CLOUD-SAMPLE] Obtained access token:', accessToken.substring(0, 10) + '...');
        
        // Store token in auth utility
        setAuth(accessToken, responseData);
        
        // Store token and user data in localStorage
        localStorage.setItem(ELocalStorageKey.Token, accessToken);
        localStorage.setItem(ELocalStorageKey.WorkspaceId, responseData.workspace_id || '');
        localStorage.setItem(ELocalStorageKey.UserId, responseData.user_id || '');
        localStorage.setItem(ELocalStorageKey.Username, responseData.username || '');
        localStorage.setItem(ELocalStorageKey.Flag, String(responseData.user_type || 2));
        
        if (responseData.workspace_name) {
          localStorage.setItem(ELocalStorageKey.WorkspaceName, responseData.workspace_name);
        }
        
        if (responseData.workspace_desc) {
          localStorage.setItem(ELocalStorageKey.WorkspaceDesc, responseData.workspace_desc);
        }
        
        if (responseData.platform_name) {
          localStorage.setItem(ELocalStorageKey.PlatformName, responseData.platform_name);
        }
        
        // Store additional data if available
        if (responseData.mqtt_addr) {
          const mqttConfig = {
            host: responseData.mqtt_addr,
            username: responseData.mqtt_username,
            password: responseData.mqtt_password,
            connectCallback: 'connectCallback'
          };
          localStorage.setItem('mqttConfig', JSON.stringify(mqttConfig));
        }
        
        if (responseData.refresh_token) {
          localStorage.setItem('refresh_token', responseData.refresh_token);
        }
        
        if (responseData.websdk_ng_install_id) {
          localStorage.setItem(ELocalStorageKey.WebsdkNgInstallId, responseData.websdk_ng_install_id);
        }
        
        try {
          // STEP 5: Set API component parameters
          console.log('[CLOUD-SAMPLE] Setting API component parameters with token');
          djiBridge.setComponentParam(EComponentName.Api, {
            host: window.location.origin,
            token: accessToken
          });
          
          // STEP 6: Load API component
          console.log('[CLOUD-SAMPLE] Loading API component');
          const apiLoaded = djiBridge.loadComponent(
            EComponentName.Api, 
            djiBridge.getComponentParam(EComponentName.Api)
          );
          
          if (!apiLoaded) {
            console.error('[CLOUD-SAMPLE] Failed to load API component');
            setError('Failed to initialize API module');
            setLoading(false);
            return;
          }
          
          // Compare token and host values with what was sent
          const sentToken = accessToken;
          const sentHost = window.location.origin;
          
          // Get current values from the bridge
          try {
            // Note: The JSBridge interface returns a JSON string that needs to be parsed
            const tokenResponse = djiBridge.getToken();
            console.log('[CLOUD-SAMPLE] Raw token response:', tokenResponse);
            const parsedTokenResponse = JSON.parse(tokenResponse);
            const currentToken = parsedTokenResponse.code === 0 ? parsedTokenResponse.data : null;
            
            const hostResponse = djiBridge.getHost();
            console.log('[CLOUD-SAMPLE] Raw host response:', hostResponse);
            const parsedHostResponse = JSON.parse(hostResponse);
            const currentHost = parsedHostResponse.code === 0 ? parsedHostResponse.data : null;
            
            // Log comparison results
            console.log('[CLOUD-SAMPLE] API Token Comparison:');
            console.log('[CLOUD-SAMPLE] Sent token:', sentToken ? `${sentToken.substring(0, 10)}...` : 'null');
            console.log('[CLOUD-SAMPLE] Current token from bridge:', currentToken ? `${currentToken.substring(0, 10)}...` : 'null');
            console.log('[CLOUD-SAMPLE] Token match:', sentToken === currentToken);
            
            console.log('[CLOUD-SAMPLE] API Host Comparison:');
            console.log('[CLOUD-SAMPLE] Sent host:', sentHost);
            console.log('[CLOUD-SAMPLE] Current host from bridge:', currentHost);
            console.log('[CLOUD-SAMPLE] Host match:', sentHost === currentHost);
          } catch (error) {
            console.error('[CLOUD-SAMPLE] Error comparing token and host values:', error);
          }
          
          // Set platform information with data from server
          const platformResult = djiBridge.platformSetInformation(
            responseData.platform_name || 'Cloud Api Platform',
            responseData.workspace_name || '',
            responseData.workspace_desc || ''
          );
          console.log('[CLOUD-SAMPLE] Platform information set result:', platformResult);
          
          // Navigate to dashboard
          setLoading(false);
          navigate('/cloud-sample-dashboard');
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error setting up API component:', error);
          setError('Failed to initialize API module: ' + error.message);
          setLoading(false);
        }
      } else {
        console.error('[CLOUD-SAMPLE] Login failed with code:', response.data.code);
        setError(response.data.message || 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('[CLOUD-SAMPLE] Login error:', err);
      console.log('[CLOUD-SAMPLE] Error response:', err.response ? err.response.data : 'No response data');
      setError(err.response?.data?.message || err.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <Container>
      <Row className="justify-content-md-center mt-5">
        <Col md={6}>
          <div className="login-container p-4 border rounded">
            <h2 className="text-center mb-4">Pilot Cloud API Demo</h2>
            
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
              
              <Button 
                variant="primary" 
                type="submit" 
                className="w-100" 
                disabled={loading || !isVerified}
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Logging in...</span>
                  </>
                ) : 'Login'}
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default CloudSamplePilotLogin; 